import React, { useState, useCallback } from 'react';
import { Settings, CheckCircle, AlertTriangle, RotateCcw, RotateCw, Trash2, Plus, Minus } from 'lucide-react';
import { CourseConfiguration, DRF_2025_QUOTAS, ObstacleType, isSpecialObstacleAllowed } from '../types/drf-rules';
import { validateCourse, createDefaultObstacle, ObstaclePool } from '../utils/course-generator';
import CourseGrid from './CourseGrid';
import ObstaclePalette from './ObstaclePalette';
import CourseSettings from './CourseSettings';
import ValidationPanel from './ValidationPanel';
import Logo from '../Assets/6787.svg';

const CourseDesigner: React.FC = () => {
  const [showSettings, setShowSettings] = useState(true);
  const [courseConfig, setCourseConfig] = useState<CourseConfiguration>({
    classCode: 'LC',
    arenaWidth: 40,
    arenaLength: 60,
    isIndoor: false,
    animalType: 'horse',
    obstacles: []
  });
  
  const [selectedObstacleType, setSelectedObstacleType] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showCoursePath, setShowCoursePath] = useState(false);
  const [selectedObstacleId, setSelectedObstacleId] = useState<string | null>(null);

  // Calculate remaining obstacles based on current state and DRF rules
  const calculateRemainingObstacles = useCallback(() => {
    const quota = DRF_2025_QUOTAS[courseConfig.classCode];
    
    // Count current obstacles by type
    const allObstacles = courseConfig.obstacles.reduce((acc, obstacle) => {
      acc[obstacle.type] = (acc[obstacle.type] || 0) + 1;
      return acc;
    }, {} as Record<ObstacleType, number>);
    
    const currentObstacles = courseConfig.obstacles
      .filter(obstacle => obstacle.type !== 'start' && obstacle.type !== 'finish')
      .reduce((acc, obstacle) => {
        acc[obstacle.type] = (acc[obstacle.type] || 0) + 1;
        return acc;
      }, {} as Record<ObstacleType, number>);
    
    // Special handling for Freestyle - allow 100 of each type
    if (courseConfig.classCode === 'Freestyle') {
      return {
        vertical: Math.max(0, 100 - (currentObstacles.vertical || 0)),
        oxer: Math.max(0, 100 - (currentObstacles.oxer || 0)),
        triple_bar: Math.max(0, 100 - (currentObstacles.triple_bar || 0)),
        liverpool: Math.max(0, 100 - (currentObstacles.liverpool || 0)),
        open_water: courseConfig.isIndoor ? 0 : Math.max(0, 100 - (currentObstacles.open_water || 0)),
        combination: Math.max(0, 100 - (currentObstacles.combination || 0)),
        start: Math.max(0, 1 - (allObstacles.start || 0)),
        finish: Math.max(0, 1 - (allObstacles.finish || 0))
      } as ObstaclePool;
    }
    
    // Use DRF percentage distribution of TOTAL obstacles (not just regular slots)
    const totalSlots = quota.maxObstacles;
    
    // Calculate allocation for each type based on DRF percentage distribution of total
    // Note: Combinations are not placeable obstacles, so distribute that 5% among others
    const verticalAllocation = Math.round(totalSlots * 0.50); // 50% of total (45% + part of combo)
    const oxerAllocation = Math.round(totalSlots * 0.43); // 43% of total (40% + part of combo)
    const tripleBarAllocation = Math.round(totalSlots * 0.07); // 7% of total (5% + part of combo)
    const liverpoolAllocation = Math.round(totalSlots * 0.05); // 5% of total
    const waterAllocation = Math.round(totalSlots * 0.05); // 5% of total
    
    return {
      vertical: Math.max(0, verticalAllocation - (currentObstacles.vertical || 0)),
      oxer: Math.max(0, oxerAllocation - (currentObstacles.oxer || 0)),
      triple_bar: Math.max(0, tripleBarAllocation - (currentObstacles.triple_bar || 0)),
      liverpool: isSpecialObstacleAllowed(courseConfig.classCode, 'liverpool') ? 
        Math.max(0, liverpoolAllocation - (currentObstacles.liverpool || 0)) : 0,
      open_water: isSpecialObstacleAllowed(courseConfig.classCode, 'open_water') && !courseConfig.isIndoor ? 
        Math.max(0, waterAllocation - (currentObstacles.open_water || 0)) : 0,
      combination: Math.max(0, quota.maxCombos - (currentObstacles.combination || 0)),
      start: Math.max(0, 1 - (allObstacles.start || 0)),
      finish: Math.max(0, 1 - (allObstacles.finish || 0))
    } as ObstaclePool;
  }, [courseConfig]);

  const remainingObstacles = calculateRemainingObstacles();

  const handleSettingsChange = useCallback((newConfig: Partial<CourseConfiguration>) => {
    const updatedConfig = { ...courseConfig, ...newConfig };
    setCourseConfig(updatedConfig);
    
    // Validate the updated course
    const errors = validateCourse(updatedConfig, countActualCombinationsForConfig(updatedConfig));
    setValidationErrors(errors);
  }, [courseConfig]);

  const handleGridClick = useCallback((x: number, y: number) => {
    if (!selectedObstacleType) return;
    
    const newObstacle = createDefaultObstacle(
      selectedObstacleType as any,
      courseConfig.classCode,
      x,
      y,
      courseConfig.obstacles
    );
    
    const updatedConfig = {
      ...courseConfig,
      obstacles: [...courseConfig.obstacles, newObstacle]
    };
    
    setCourseConfig(updatedConfig);
    // Calculate combinations for the updated config
    const updatedCombinations = countActualCombinationsForConfig(updatedConfig);
    setValidationErrors(validateCourse(updatedConfig, updatedCombinations));
  }, [selectedObstacleType, courseConfig]);

  const handleObstacleMove = useCallback((obstacleId: string, newX: number, newY: number) => {
    const updatedConfig = {
      ...courseConfig,
      obstacles: courseConfig.obstacles.map(obstacle =>
        obstacle.id === obstacleId 
          ? { ...obstacle, x: newX, y: newY }
          : obstacle
      )
    };
    
    setCourseConfig(updatedConfig);
    setValidationErrors(validateCourse(updatedConfig, countActualCombinationsForConfig(updatedConfig)));
  }, [courseConfig]);

  const handleObstacleRotate = useCallback((obstacleId: string) => {
    const updatedConfig = {
      ...courseConfig,
      obstacles: courseConfig.obstacles.map(obstacle =>
        obstacle.id === obstacleId 
          ? { ...obstacle, rotation: (obstacle.rotation + 45) % 360 }
          : obstacle
      )
    };
    
    setCourseConfig(updatedConfig);
    setValidationErrors(validateCourse(updatedConfig, countActualCombinationsForConfig(updatedConfig)));
  }, [courseConfig]);

  const handleObstacleRotateCounterClockwise = useCallback((obstacleId: string) => {
    const updatedConfig = {
      ...courseConfig,
      obstacles: courseConfig.obstacles.map(obstacle =>
        obstacle.id === obstacleId 
          ? { ...obstacle, rotation: (obstacle.rotation - 45 + 360) % 360 }
          : obstacle
      )
    };
    
    setCourseConfig(updatedConfig);
    setValidationErrors(validateCourse(updatedConfig, countActualCombinationsForConfig(updatedConfig)));
  }, [courseConfig]);

  const handleObstacleDelete = useCallback((obstacleId: string) => {
    const updatedConfig = {
      ...courseConfig,
      obstacles: courseConfig.obstacles.filter(obstacle => obstacle.id !== obstacleId)
    };
    
    setCourseConfig(updatedConfig);
    setValidationErrors(validateCourse(updatedConfig, countActualCombinationsForConfig(updatedConfig)));
  }, [courseConfig]);

  const handleClearCourse = useCallback(() => {
    const clearedConfig = { ...courseConfig, obstacles: [] };
    setCourseConfig(clearedConfig);
    setValidationErrors(validateCourse(clearedConfig, countActualCombinationsForConfig(clearedConfig)));
  }, [courseConfig]);

  const handleObstacleNumberIncrease = useCallback((obstacleId: string) => {
    const obstacle = courseConfig.obstacles.find(o => o.id === obstacleId);
    if (!obstacle || !obstacle.number) return;
    
    const currentNumber = obstacle.number;
    const targetNumber = currentNumber + 1;
    
    // Find the obstacle with the target number
    const targetObstacle = courseConfig.obstacles.find(o => 
      o.number === targetNumber && o.type !== 'start' && o.type !== 'finish'
    );
    
    if (!targetObstacle) return; // No obstacle to swap with
    
    // Simple swap: current becomes target number, target becomes current number
    const updatedConfig = {
      ...courseConfig,
      obstacles: courseConfig.obstacles.map(obs => {
        if (obs.id === obstacleId) {
          return { ...obs, number: targetNumber };
        }
        if (obs.id === targetObstacle.id) {
          return { ...obs, number: currentNumber };
        }
        return obs;
      })
    };
    
    setCourseConfig(updatedConfig);
    setValidationErrors(validateCourse(updatedConfig, countActualCombinationsForConfig(updatedConfig)));
  }, [courseConfig]);

  const handleObstacleNumberDecrease = useCallback((obstacleId: string) => {
    const obstacle = courseConfig.obstacles.find(o => o.id === obstacleId);
    if (!obstacle || !obstacle.number || obstacle.number <= 1) return;
    
    const currentNumber = obstacle.number;
    const targetNumber = currentNumber - 1;
    
    // Find the obstacle with the target number
    const targetObstacle = courseConfig.obstacles.find(o => 
      o.number === targetNumber && o.type !== 'start' && o.type !== 'finish'
    );
    
    if (!targetObstacle) return; // No obstacle to swap with
    
    // Simple swap: current becomes target number, target becomes current number
    const updatedConfig = {
      ...courseConfig,
      obstacles: courseConfig.obstacles.map(obs => {
        if (obs.id === obstacleId) {
          return { ...obs, number: targetNumber };
        }
        if (obs.id === targetObstacle.id) {
          return { ...obs, number: currentNumber };
        }
        return obs;
      })
    };
    
    setCourseConfig(updatedConfig);
    setValidationErrors(validateCourse(updatedConfig, countActualCombinationsForConfig(updatedConfig)));
  }, [courseConfig]);

  const handleObstacleHeightChange = useCallback((obstacleId: string, newHeight: number) => {
    const quota = DRF_2025_QUOTAS[courseConfig.classCode];
    
    // Ensure height is within class limits
    const clampedHeight = Math.max(quota.heightMin, Math.min(quota.heightMax, newHeight));
    
    const updatedConfig = {
      ...courseConfig,
      obstacles: courseConfig.obstacles.map(obstacle =>
        obstacle.id === obstacleId 
          ? { ...obstacle, height: clampedHeight }
          : obstacle
      )
    };
    
    setCourseConfig(updatedConfig);
    setValidationErrors(validateCourse(updatedConfig, countActualCombinationsForConfig(updatedConfig)));
  }, [courseConfig]);

  const handleObstacleSelect = useCallback((obstacleId: string | null) => {
    const obstacle = obstacleId ? courseConfig.obstacles.find(o => o.id === obstacleId) || null : null;
    setSelectedObstacleId(obstacle?.id || null);
  }, [courseConfig.obstacles]);

  // Helper function to calculate distance between two points
  const calculateDistance = (x1: number, y1: number, x2: number, y2: number): number => {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  };

  // Count actual combinations based on distance for any config
  const countActualCombinationsForConfig = (config: CourseConfiguration): number => {
    const startObstacle = config.obstacles.find(o => o.type === 'start');
    const finishObstacle = config.obstacles.find(o => o.type === 'finish');
    
    // Get numbered obstacles and sort by number
    const numberedObstacles = config.obstacles
      .filter(o => o.number && o.type !== 'start' && o.type !== 'finish')
      .sort((a, b) => (a.number || 0) - (b.number || 0));
    
    const sequence: any[] = [];
    if (startObstacle) sequence.push(startObstacle);
    sequence.push(...numberedObstacles);
    if (finishObstacle) sequence.push(finishObstacle);
    
    if (sequence.length < 2) return 0;
    
    let combinationCount = 0;
    
    for (let i = 0; i < sequence.length - 1; i++) {
      const from = sequence[i];
      const to = sequence[i + 1];
      
      // Calculate segment distance
      const segmentDistance = calculateDistance(from.x, from.y, to.x, to.y);
      const curveMultiplier = segmentDistance < 15 ? 1.2 : 1.1;
      const displayDistance = segmentDistance * curveMultiplier;
      
      // Combination check: Only between numbered jumps (not start/finish)
      const isCombination = (from.type !== 'start' && from.type !== 'finish' && 
                            to.type !== 'start' && to.type !== 'finish') &&
                           (config.animalType === 'horse' 
                             ? (displayDistance >= 6 && displayDistance <= 12)
                             : (displayDistance >= 5 && displayDistance <= 11));
      
      if (isCombination) {
        combinationCount++;
      }
    }
    
    return combinationCount;
  };

  // Count actual combinations based on distance (same logic as CourseGrid)
  const countActualCombinations = (): number => {
    return countActualCombinationsForConfig(courseConfig);
  };

  const quota = DRF_2025_QUOTAS[courseConfig.classCode];
  const isValid = validationErrors.length === 0;
  const actualObstacleCount = courseConfig.obstacles.filter(obstacle => 
    obstacle.type !== 'start' && obstacle.type !== 'finish'
  ).length;

  // SelectedJumpControls component
  const SelectedJumpControls: React.FC = () => {
    if (!selectedObstacleId) {
      return (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Spring Kontroller</h3>
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Settings className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500">Klik på et spring for at redigere det</p>
          </div>
        </div>
      );
    }

    const selectedObstacle = courseConfig.obstacles.find(o => o.id === selectedObstacleId) || null;

    if (!selectedObstacle) {
      return (
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Spring Kontroller</h3>
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <Settings className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500">Klik på et spring for at redigere det</p>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Spring Kontroller</h3>
        
        {/* Selected jump info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-gray-900">
                {selectedObstacle.type === 'start' ? 'Start' : 
                 selectedObstacle.type === 'finish' ? 'Slut' :
                 selectedObstacle.number ? `Spring ${selectedObstacle.number}` : 'Spring'}
              </h4>
            </div>
            <div className="w-8 h-8 rounded border-2 flex items-center justify-center text-white font-bold text-xs" 
                 style={{ backgroundColor: getObstacleColor(selectedObstacle.type) }}>
              {selectedObstacle.type === 'vertical' ? 'V' :
               selectedObstacle.type === 'oxer' ? 'O' :
               selectedObstacle.type === 'triple_bar' ? 'T' :
               selectedObstacle.type === 'liverpool' ? 'L' :
               selectedObstacle.type === 'open_water' ? 'W' :
               selectedObstacle.type === 'start' ? 'S' :
               selectedObstacle.type === 'finish' ? 'F' : '?'}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="space-y-4">
          {/* Height control - only for actual jumps */}
          {selectedObstacle.type !== 'start' && selectedObstacle.type !== 'finish' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Højde (cm)</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min={quota.heightMin}
                  max={quota.heightMax}
                  value={selectedObstacle.height}
                  onChange={(e) => handleObstacleHeightChange(selectedObstacle.id, parseInt(e.target.value) || selectedObstacle.height)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-sm text-gray-500">
                  {quota.heightMin}-{quota.heightMax}cm
                </span>
              </div>
            </div>
          )}

          {/* Rotation control */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rotation</label>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleObstacleRotate(selectedObstacle.id)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center space-x-2"
              >
                <RotateCw className="w-4 h-4" />
                <span>Roter 45°</span>
              </button>
              <button
                onClick={() => handleObstacleRotateCounterClockwise(selectedObstacle.id)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center space-x-2"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Roter -45°</span>
              </button>
            </div>
          </div>

          {/* Number controls - only for actual jumps */}
          {selectedObstacle.number && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Spring Nummer</label>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleObstacleNumberDecrease(selectedObstacle.id)}
                  disabled={selectedObstacle.number <= 1}
                  className="px-3 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-4 py-2 bg-gray-100 rounded-md text-center min-w-[60px] font-medium">
                  {selectedObstacle.number}
                </span>
                <button
                  onClick={() => handleObstacleNumberIncrease(selectedObstacle.id)}
                  className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Delete button */}
          <div>
            <button
              onClick={() => {
                handleObstacleDelete(selectedObstacle.id);
                setSelectedObstacleId(null);
              }}
              className="w-full px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 flex items-center justify-center space-x-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>Slet Spring</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to get obstacle color (same as in CourseGrid)
  const getObstacleColor = (type: ObstacleType): string => {
    switch (type) {
      case 'vertical': return '#8B5CF6';
      case 'oxer': return '#F59E0B';
      case 'triple_bar': return '#10B981';
      case 'liverpool': return '#3B82F6';
      case 'open_water': return '#06B6D4';
      case 'combination': return '#EF4444';
      case 'start': return '#22C55E';
      case 'finish': return '#EF4444';
      default: return '#6B7280';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img src={Logo} alt="Springbanedesigner Logo" className="w-12 h-12" />
              <div>
                <p className="text-gray-600 text-sm">
                  Dansk springbane designer - {courseConfig.classCode} klasse
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                {isValid ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                )}
                <span className="text-sm text-gray-600">
                  {actualObstacleCount}/{quota.minObstacles}-{quota.maxObstacles} spring
                </span>
              </div>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={() => setShowCoursePath(!showCoursePath)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  showCoursePath 
                    ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Bane rute
              </button>
              <button
                onClick={handleClearCourse}
                className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          
          {/* Settings Panel */}
          {showSettings && (
            <div className="lg:col-span-1">
              <CourseSettings
                config={courseConfig}
                onChange={handleSettingsChange}
              />
            </div>
          )}
          
          {/* Course Grid */}
          <div className={`${showSettings ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <CourseGrid
                config={courseConfig}
                onGridClick={handleGridClick}
                onObstacleMove={handleObstacleMove}
                onObstacleRotate={handleObstacleRotate}
                onObstacleRotateCounterClockwise={handleObstacleRotateCounterClockwise}
                onObstacleDelete={handleObstacleDelete}
                onObstacleNumberIncrease={handleObstacleNumberIncrease}
                onObstacleNumberDecrease={handleObstacleNumberDecrease}
                onObstacleHeightChange={handleObstacleHeightChange}
                onObstacleSelect={handleObstacleSelect}
                selectedTool={selectedObstacleType}
                showCoursePath={showCoursePath}
              />
            </div>
          </div>
          
          {/* Tools Panel */}
          <div className="lg:col-span-1 space-y-6">
            <SelectedJumpControls />
            
            <ObstaclePalette
              pool={remainingObstacles}
              classCode={courseConfig.classCode}
              selectedType={selectedObstacleType}
              onTypeSelect={setSelectedObstacleType}
            />
            
            <ValidationPanel
              errors={validationErrors}
              quota={quota}
              courseConfig={courseConfig}
              actualCombinations={countActualCombinations()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDesigner; 